/**
 * What each row can do, by state (DEC-191 decision 1, amended by DEC-192):
 *
 *   Finished (and with gaps): Open results, Re-run or Upload again, Archive
 *   Stopped by you:           Open progress, Re-run or Upload again, Archive
 *   Running:                  Watch live, Pause, Stop
 *   Paused:                   Resume, Stop; after the workspace's waiting time also
 *                             Archive and Delete permanently
 *   Waiting to start:         Cancel (a queued scan) or Cancel (a deferred request)
 *   Failed:                   See why (the What happened pop-up, DEC-197), Try again
 *                             only when retryable, Archive
 *   Archived:                 Resume (paused only), Open results (finished only),
 *                             Restore, Delete permanently
 *   Deleted record:           nothing; it has no results to open
 *
 * The dashboard no longer offers the old Delete (DELETE /scans/{id}): DEC-192 replaced it
 * with Archive, then Delete permanently from Archived. DELETE stays as Stop.
 *
 * "Open results" goes to the reader's own report view, never one their profile cannot
 * open (DEC-190 decision 7). An uploaded folder is not kept, so it is never re-run: it
 * is picked again (DEC-180).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { scansApi } from '../../api/scans'
import { useAuth } from '../../hooks/useAuth'
import { invalidateScanQueries } from '../../hooks/useScanQueries'
import type { ScanStatusResponse } from '../../types/api'
import { isUploadScan } from '../../utils/format'
import { resultsPath } from '../layout/reportViews'
import type { DashItem, RowAction } from './ScanRow'
import { archiveOffered, erasedCount, purgeConfirmText, repoLabel, whenLabel } from './scanFormat'

export interface Notice {
  tone: 'info' | 'error'
  text: string
}

export function useScanActions(opts: { onSeeWhy?: (scan: ScanStatusResponse) => void } = {}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { claims } = useAuth()
  const profile = claims?.profile ?? null
  const [notice, setNotice] = useState<Notice | null>(null)

  const refresh = () => invalidateScanQueries(queryClient)

  async function run(work: () => Promise<unknown>, ok?: string) {
    try {
      await work()
      if (ok) setNotice({ tone: 'info', text: ok })
    } catch (err) {
      setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'That did not work. Try again.' })
    } finally {
      void refresh()
    }
  }

  function rerun(scan: ScanStatusResponse) {
    const name = repoLabel(scan.repo_url)
    void run(async () => {
      const created = await scansApi.create({
        repo_url: scan.repo_url,
        branch: scan.branch,
        triggered_by: 'manual',
        use_llm: scan.use_llm,
      } as never)
      // DEC-123: over the limit the scan is queued and has no id yet. Said, not hidden.
      if (!created.scan_id) {
        setNotice({
          tone: 'info',
          text: created.reason ?? `A new scan of ${name} is queued and starts when a running scan finishes.`,
        })
        return
      }
      setNotice({ tone: 'info', text: `A new scan of ${name} on ${scan.branch} has started.` })
    })
  }

  function againAction(scan: ScanStatusResponse, label = 'Re-run'): RowAction {
    if (isUploadScan(scan.repo_url)) {
      return { label: 'Upload again', tone: 'secondary', onClick: () => navigate('/connect?mode=upload') }
    }
    return { label, tone: 'secondary', onClick: () => rerun(scan) }
  }

  function archiveAction(scan: ScanStatusResponse): RowAction {
    const name = repoLabel(scan.repo_url)
    return {
      label: 'Archive',
      tone: 'secondary',
      onClick: () =>
        void run(
          () => scansApi.archive(scan.scan_id),
          `Archived the ${whenLabel(scan.started_at)} scan of ${name}. It keeps everything and is in the Archived tab.`,
        ),
    }
  }

  function restoreAction(scan: ScanStatusResponse): RowAction {
    return {
      label: 'Restore',
      tone: 'secondary',
      onClick: () =>
        void run(() => scansApi.unarchive(scan.scan_id), `${repoLabel(scan.repo_url)} is back in the main lists.`),
    }
  }

  function purgeAction(scan: ScanStatusResponse): RowAction {
    const name = repoLabel(scan.repo_url)
    return {
      label: 'Delete permanently',
      tone: 'danger',
      confirm: { text: purgeConfirmText(erasedCount(scan)), yesLabel: 'Delete permanently' },
      onClick: () =>
        void run(
          () => scansApi.purge(scan.scan_id),
          `Deleted the ${whenLabel(scan.started_at)} scan of ${name} permanently. Its record stays in Archived.`,
        ),
    }
  }

  function resumeAction(scan: ScanStatusResponse): RowAction {
    return {
      label: 'Resume',
      tone: 'primary',
      onClick: () =>
        void run(
          () => scansApi.resume(scan.scan_id),
          scan.archived_at
            ? `${repoLabel(scan.repo_url)} resumed and is back in the main lists.`
            : `${repoLabel(scan.repo_url)} resumed from where it paused.`,
        ),
    }
  }

  function archivedActions(scan: ScanStatusResponse, status: DashItem['status']): RowAction[] {
    if (scan.deleted_at) return []
    const open: RowAction[] =
      status === 'complete' || status === 'partial'
        ? [{ label: 'Open results', tone: 'secondary', onClick: () => navigate(resultsPath(profile, scan.scan_id)) }]
        : []
    return [...(status === 'paused' ? [resumeAction(scan)] : []), ...open, restoreAction(scan), purgeAction(scan)]
  }

  function stopAction(scan: ScanStatusResponse): RowAction {
    // DEC-040: DELETE doubles as Stop on a scan that has not finished.
    return {
      label: 'Stop',
      tone: 'danger',
      confirm: {
        text: 'Stop this scan? Agents that have not started will not run, and what it found so far is discarded. It stays in the list as "Stopped by you".',
        yesLabel: 'Stop scan',
      },
      onClick: () => void run(() => scansApi.deleteScan(scan.scan_id), `${repoLabel(scan.repo_url)} was stopped.`),
    }
  }

  function scanActions(scan: ScanStatusResponse, status: DashItem['status']): RowAction[] {
    const monitor = () => navigate(`/scans/${scan.scan_id}/monitor`)
    if (scan.deleted_at || scan.archived_at) return archivedActions(scan, status)
    switch (status) {
      case 'running':
        return [
          { label: 'Watch live', tone: 'primary', onClick: monitor },
          {
            label: 'Pause',
            tone: 'secondary',
            onClick: () => void run(() => scansApi.pause(scan.scan_id), `${repoLabel(scan.repo_url)} pauses once the agents at work finish their step.`),
          },
          stopAction(scan),
        ]
      case 'paused':
        return [
          resumeAction(scan),
          stopAction(scan),
          // DEC-192 decision 1: only once the workspace's waiting time has passed.
          ...(archiveOffered(scan) ? [archiveAction(scan), purgeAction(scan)] : []),
        ]
      case 'queued':
        return [
          {
            label: 'Cancel',
            tone: 'danger',
            confirm: {
              text: 'Cancel this scan before it starts? It stays in the list as "Stopped by you".',
              yesLabel: 'Cancel scan',
            },
            onClick: () => void run(() => scansApi.deleteScan(scan.scan_id), `${repoLabel(scan.repo_url)} was cancelled before it started.`),
          },
        ]
      case 'failed':
        return [
          { label: 'See why', tone: 'primary', onClick: opts.onSeeWhy ? () => opts.onSeeWhy!(scan) : monitor },
          ...(scan.failure_is_retryable ? [againAction(scan, 'Try again')] : []),
          archiveAction(scan),
        ]
      case 'stopped':
        return [{ label: 'Open progress', tone: 'secondary', onClick: monitor }, againAction(scan), archiveAction(scan)]
      default:
        return [
          { label: 'Open results', tone: 'primary', onClick: () => navigate(resultsPath(profile, scan.scan_id)) },
          // DEC-197: a scan that finished with gaps says what is missing, one click away.
          ...(status === 'partial' && opts.onSeeWhy
            ? [{ label: 'What happened', tone: 'secondary' as const, onClick: () => opts.onSeeWhy!(scan) }]
            : []),
          againAction(scan),
          archiveAction(scan),
        ]
    }
  }

  function actionsFor(item: DashItem): RowAction[] {
    if (item.kind === 'scan') return scanActions(item.scan, item.status)
    const p = item.pending
    return [
      {
        label: 'Cancel',
        tone: 'danger',
        disabled: p.starting,
        confirm: {
          text:
            p.trigger_count > 1
              ? `Cancel this scan before it starts? The ${p.trigger_count} requests it stands for will not be scanned until the next one.`
              : 'Cancel this scan before it starts? Nothing is scanned until it is requested again.',
          yesLabel: 'Cancel it',
        },
        onClick: () => void run(() => scansApi.cancelPending(p.request_id), `The waiting scan of ${repoLabel(p.repo_url)} was cancelled.`),
      },
    ]
  }

  return { actionsFor, scanActions, againAction, notice, setNotice, profile }
}
